import { useEffect, useState } from 'react';
import { ITypedBaseResponse } from '@nkr/models/base-response';
import { IOpoAdminSettings, IOpoAdminSettingsRequest, IOpoAgreement } from '@nkr/models/opo-admin-settings';
import { downloadFile } from '@nkr/services/download-file';
import { AppActionCreators } from '@nkr/store/application';
import { AuthActionCreators } from '@nkr/store/authorization';
import { useAppHookContext } from '@nkr/utils/rest-api';
import usePortalOpo from './use-portal-opo';

export interface IOpoAdminSaveResult {
    success: boolean,
    message: string
}

interface IRetVal {
    settings: IOpoAdminSettings | null,
    agreements: IOpoAgreement[],
    loading: boolean,
    error: string,
    save: (request: IOpoAdminSettingsRequest) => Promise<IOpoAdminSaveResult>,
    uploadAgreement: (agreement: IOpoAgreement, file: File) => Promise<IOpoAdminSaveResult>,
    downloadAgreement: (agreement: IOpoAgreement) => Promise<void>
}

const useOpoAdminSettings = (): IRetVal => {
    const { dispatch } = useAppHookContext();
    const { opoId } = usePortalOpo();
    const [settings, setSettings] = useState<IOpoAdminSettings | null>(null);
    const [agreements, setAgreements] = useState<IOpoAgreement[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    const baseUrl = `${__API__}/opo/${opoId}`;

    const getAgreements = (): Promise<ITypedBaseResponse<IOpoAgreement[]>> => dispatch(AuthActionCreators.apiGet({ url: `${baseUrl}/agreements` }));

    useEffect(() => {
        if (!opoId) {
            return;
        }

        let isCurrent = true;

        const fetchData = async () => {
            setLoading(true);
            setError('');

            await dispatch(AppActionCreators.showLoadingAsync('Getting admin settings, one moment please...'));

            try {
                const [settingsResponse, agreementsResponse]: [ITypedBaseResponse<IOpoAdminSettings>, ITypedBaseResponse<IOpoAgreement[]>] = await Promise.all([
                    dispatch(AuthActionCreators.apiGet({ url: `${baseUrl}/admin-settings` })),
                    getAgreements()
                ]);

                if (!isCurrent) {
                    return;
                }

                if (settingsResponse && settingsResponse.data.success && agreementsResponse && agreementsResponse.data.success) {
                    setSettings(settingsResponse.data.results);
                    setAgreements(agreementsResponse.data.results);
                } else {
                    setError('Unable to load admin settings');
                }
            } catch (err) {
                if (isCurrent) {
                    setError('Unable to load admin settings');
                }
            } finally {
                if (isCurrent) {
                    setLoading(false);
                    dispatch(AppActionCreators.hideLoading());
                }
            }
        };

        fetchData();

        return () => {
            isCurrent = false;
            dispatch(AppActionCreators.hideLoading());
        };
    }, [opoId]);

    const save = async (request: IOpoAdminSettingsRequest): Promise<IOpoAdminSaveResult> => {
        await dispatch(AppActionCreators.showLoadingAsync('Saving admin settings, one moment please...'));

        try {
            const response: ITypedBaseResponse<IOpoAdminSettings> = await dispatch(AuthActionCreators.apiPut({ url: `${baseUrl}/admin-settings`, data: request }));

            if (response && response.data.success) {
                setSettings(response.data.results);
                return { success: true, message: '' };
            }

            return { success: false, message: (response && response.data.message) || 'Unable to save admin settings' };
        } catch (err) {
            return { success: false, message: 'Unable to save admin settings' };
        } finally {
            dispatch(AppActionCreators.hideLoading());
        }
    };

    const uploadAgreement = async (agreement: IOpoAgreement, file: File): Promise<IOpoAdminSaveResult> => {
        await dispatch(AppActionCreators.showLoadingAsync('Uploading file, one moment please...'));

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response: ITypedBaseResponse<null> = await dispatch(AuthActionCreators.apiPostFormData({ url: `${baseUrl}/agreements/${agreement.documentTypeId}`, data: formData }));

            if (response && response.data.success) {
                const agreementsResponse = await getAgreements();

                if (agreementsResponse && agreementsResponse.data.success) {
                    setAgreements(agreementsResponse.data.results);
                }

                return { success: true, message: '' };
            }

            return { success: false, message: (response && response.data.message) || 'Unable to upload the file' };
        } catch (err) {
            return { success: false, message: 'Unable to upload the file' };
        } finally {
            dispatch(AppActionCreators.hideLoading());
        }
    };

    const downloadAgreement = async (agreement: IOpoAgreement) => {
        const response: any = await dispatch(AuthActionCreators.apiGet({ url: `${baseUrl}/agreements/${agreement.documentTypeId}/download`, responseType: 'blob' }));

        downloadFile(response.data, `${agreement.name.toLowerCase().replace(/ /g, '-')}-${opoId}.pdf`, 'application/pdf');
    };

    return { settings, agreements, loading, error, save, uploadAgreement, downloadAgreement };
}

export default useOpoAdminSettings;
